import { ReactElement, useState } from 'react';
import { Calendar, Leader, OrgUserAutocompleteOption } from '../../../store';

import { trackEvent } from '../../../utils/appAnalyticsUtils';
import { EVENT_TYPE } from '../../../constants';
import { CabAutocomplete, CabButton, CabTextInput, CabImageUpload } from '@CabComponents';
import { FormControl, FormHelperText, FormLabel, styled } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { getLeaderTemplate } from '../../../store/templates';
import { CabModal } from '@CabComponents/CabModal';
import { validateEmail } from '../../../utils/inputUtils';
import { useCabinetText } from '../../../CabinetContext';
import colors from '../../../colors';
import { MissingCalendars } from '../../ManageCalendars/MissingCalendars';
import { CalendarAccessIndicators } from '../../CalendarAccessIndicators';
import { getCalendarDisplayNamesById } from '../../../utils/scheduleUtils';


export interface NewLeader extends Leader {
  img?: File | null;
  imgName?: string;
  calendars?: number[];
}

interface Props {
  handleSaveLeader: (data: Leader) => void;
  newLeader?: Leader;
  firstLeader: boolean;
  cancelAddLeader: () => void;
  nextDisabled: boolean;
  isOpen: boolean;
  calendars?: Calendar[];
  shareWithUserOptions?: OrgUserAutocompleteOption[];
  onGetEmailCalendars?: (email: string) => Promise<Calendar[]>;
}

const StyleCabTextInput = styled(CabTextInput, {label: "StyledCabTextInput"})({
  width: "100%",
  marginTop: 12
});



export const LeaderInfo =
({ handleSaveLeader, newLeader, isOpen, cancelAddLeader, calendars, shareWithUserOptions, onGetEmailCalendars
}: Props): ReactElement => {

  const [modalTitle, buttonText] = useCabinetText([
    'add-team-member-title',
    'add-team-member-button',
  ]);



  const defaultValues: NewLeader = newLeader ? newLeader : getLeaderTemplate();
  defaultValues["img"] = null;
  defaultValues["calendars"] = [];
  const [additionalCalendars, setAdditionalCalendars] = useState<{ [eml: string]: Calendar[] }>({});
  const calendarDisplayNamesById = calendars ? getCalendarDisplayNamesById(calendars) : {};

  const meetingForm = useForm< NewLeader>({ defaultValues });
  const { control, handleSubmit: formSubmit, setValue, watch, formState } = meetingForm;

  const [pic_url, first_name, last_name, primary_assistant] = 
  watch(["pic_url", "first_name", "last_name", "primary_assistant"]);

  const handleSubmit = async (data:  NewLeader) => {
    handleSaveLeader(data);
    trackEvent(EVENT_TYPE.CREATE_EXEC);
    closeModal();
  };
  
  const closeModal = () => {
    meetingForm.reset();
    cancelAddLeader();
  };
  


  return (
    <CabModal 
      open={isOpen} 
      onClose={closeModal} 
      title={modalTitle} 
      closeIcon 
      closeOnBackdropClick
      actionButtons={
        <CabButton
          onClick={formSubmit(handleSubmit)} 
          disabled={!formState.isValid || !(!shareWithUserOptions || (primary_assistant && primary_assistant > 0))}
        >
          {buttonText}
        </CabButton>
      }
    >
      <form id='leader-info-form'>
        <Controller name="first_name" control={control} rules={{ required: true }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <StyleCabTextInput
                {...field}
                inputRef={ref}
                label="First Name"
                required
              />
              <FormHelperText error>{error && 'First name is required'}</FormHelperText>
            </>
          )} />
        <Controller name="last_name" control={control} rules={{ required: true }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <StyleCabTextInput
                {...field}
                inputRef={ref}
                label="Last Name"
                required
              />
              <FormHelperText error>{error && 'Last name is required'}</FormHelperText>
            </>
          )} />
        <Controller name="email" control={control} rules={{ required: true, validate: validateEmail }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <>
              <StyleCabTextInput
                {...field}
                inputRef={ref}
                type="email"
                label="Email"
                required
              />
              <FormHelperText error>
                {error?.type === 'required' ? 'Email is required'
                  : error?.type === 'validate' ? 'Must be a valid email'
                    : ''}
              </FormHelperText>
            </>
          )} />
        {calendars && (
          <FormControl fullWidth sx={{marginTop: 1}}>
            <FormLabel>Attach Calendar(s)</FormLabel>
            <Controller name="calendars" control={control} render={({ field: { ref, ...field } }) => (
              <CabAutocomplete<number, never>
                {...field}
                placeholder="Select a calendar"
                multiple
                sx={{ width: 'auto' }}
                options={calendars.map((calendar) => ({
                  value: calendar.id,
                  label: calendarDisplayNamesById[calendar.id],
                  icon: <CalendarAccessIndicators calendar={calendar} reserveColumns />,
                  color: calendar.backgroundColor
                }))}
                optionIconPlacement="end"
              />
            )} />
            {onGetEmailCalendars && (
              <MissingCalendars
                variant="inlineLink"
                handleSetAdditionalCalendars={setAdditionalCalendars}
                additionalCalendars={additionalCalendars}
                onGetEmailCalendars={onGetEmailCalendars}
                additionalDelegateEmails={[]}
                loading={false}
              />
            )}
          </FormControl>
        )}
        {shareWithUserOptions && (
          <FormControl fullWidth sx={{marginTop: 1}}>
            <FormLabel>Primary Support*</FormLabel>
            <Controller name="primary_assistant" control={control}
              render={({ field: { ref, ...field } }) => (
                <CabAutocomplete<number, never>
                  {...field}
                  placeholder={'Find teammates by name or email'}
                  options={shareWithUserOptions}
                />
              )} />
          </FormControl>
        )}
        <CabImageUpload
          currentImage={pic_url}
          avatarName={`${first_name} ${last_name}`}
          displayMode="avatar"
          avatarSize="intermediate"
          avatarColor={defaultValues.color}
          onImageSelect={(file) => {
            setValue("imgName", file.name);
            setValue("img", file);
          }}
          useDropzone={true}
          dragDropText="Drag & drop image or"
          clickText="click"
          fileTypeText="JPG or PNG. Max"
          maxSizeText="2.5Mb"
          sx={{ marginTop: 3, marginBottom: 2 }}
          dropzoneSx={{
            border: `1px solid ${colors.black200}`,
            borderRadius: 4,
            padding: 2
          }}
        />
      </form>
    </CabModal>
  );
};

export default LeaderInfo;
